/**
 * i18n bootstrap.
 *
 * - EN is the source language; EL + RU carry real translations (Phase 8).
 *   Intentional loanwords are kept in English consistently across locales
 *   (STANDARDS §15): SMS, Sender ID, credit(s), NPS, QR, brand/example values,
 *   the "Email" field label. New EN keys are backfilled into EL/RU verbatim by
 *   `scripts/generate-stubs.mjs` until a translation lands.
 * - Detection order: cookie → localStorage → browser Accept-Language
 *   → 'en' fallback. Server-stored User.language overrides via
 *   useLanguage().setLanguage at app boot (see useLanguage hook).
 * - Locale namespacing: split per major area, not per page (common, auth,
 *   settings, reviews, campaigns, contacts, onboarding, billing, dashboard,
 *   account, templates, sms-flow, locations).
 */
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enSettings from './locales/en/settings.json'
import enReviews from './locales/en/reviews.json'
import enCampaigns from './locales/en/campaigns.json'
import enContacts from './locales/en/contacts.json'
import enOnboarding from './locales/en/onboarding.json'
import enBilling from './locales/en/billing.json'
import enDashboard from './locales/en/dashboard.json'
import enAccount from './locales/en/account.json'
import enTemplates from './locales/en/templates.json'
import enSmsFlow from './locales/en/sms-flow.json'
import enLocations from './locales/en/locations.json'
import enPublic from './locales/en/public.json'

import elCommon from './locales/el/common.json'
import elAuth from './locales/el/auth.json'
import elSettings from './locales/el/settings.json'
import elReviews from './locales/el/reviews.json'
import elCampaigns from './locales/el/campaigns.json'
import elContacts from './locales/el/contacts.json'
import elOnboarding from './locales/el/onboarding.json'
import elBilling from './locales/el/billing.json'
import elDashboard from './locales/el/dashboard.json'
import elAccount from './locales/el/account.json'
import elTemplates from './locales/el/templates.json'
import elSmsFlow from './locales/el/sms-flow.json'
import elLocations from './locales/el/locations.json'
import elPublic from './locales/el/public.json'

import ruCommon from './locales/ru/common.json'
import ruAuth from './locales/ru/auth.json'
import ruSettings from './locales/ru/settings.json'
import ruReviews from './locales/ru/reviews.json'
import ruCampaigns from './locales/ru/campaigns.json'
import ruContacts from './locales/ru/contacts.json'
import ruOnboarding from './locales/ru/onboarding.json'
import ruBilling from './locales/ru/billing.json'
import ruDashboard from './locales/ru/dashboard.json'
import ruAccount from './locales/ru/account.json'
import ruTemplates from './locales/ru/templates.json'
import ruSmsFlow from './locales/ru/sms-flow.json'
import ruLocations from './locales/ru/locations.json'
import ruPublic from './locales/ru/public.json'

export const SUPPORTED_LANGUAGES = ['en', 'el', 'ru'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false }, // React already escapes
    defaultNS: 'common',
    ns: ['common', 'auth', 'settings', 'reviews', 'campaigns', 'contacts', 'onboarding', 'billing', 'dashboard', 'account', 'templates', 'sms-flow', 'locations', 'public'],
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: 'rg-lang',
      lookupLocalStorage: 'rg-lang',
      cookieMinutes: 60 * 24 * 365,
    },
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        settings: enSettings,
        reviews: enReviews,
        campaigns: enCampaigns,
        contacts: enContacts,
        onboarding: enOnboarding,
        billing: enBilling,
        dashboard: enDashboard,
        account: enAccount,
        templates: enTemplates,
        'sms-flow': enSmsFlow,
        locations: enLocations,
        public: enPublic,
      },
      el: {
        common: elCommon,
        auth: elAuth,
        settings: elSettings,
        reviews: elReviews,
        campaigns: elCampaigns,
        contacts: elContacts,
        onboarding: elOnboarding,
        billing: elBilling,
        dashboard: elDashboard,
        account: elAccount,
        templates: elTemplates,
        'sms-flow': elSmsFlow,
        locations: elLocations,
        public: elPublic,
      },
      ru: {
        common: ruCommon,
        auth: ruAuth,
        settings: ruSettings,
        reviews: ruReviews,
        campaigns: ruCampaigns,
        contacts: ruContacts,
        onboarding: ruOnboarding,
        billing: ruBilling,
        dashboard: ruDashboard,
        account: ruAccount,
        templates: ruTemplates,
        'sms-flow': ruSmsFlow,
        locations: ruLocations,
        public: ruPublic,
      },
    },
  })

// Keep <html lang> in step with the active language. Screen readers pick the
// pronunciation dictionary from this attribute and browsers use it to decide
// whether to offer translation — leaving it hardcoded to "en" makes a Greek
// or Russian page announce with an English voice.
const syncDocumentLang = (lng: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng
}
syncDocumentLang(i18n.resolvedLanguage ?? i18n.language ?? 'en')
i18n.on('languageChanged', syncDocumentLang)

export default i18n
